import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import {
  cancelInvitation,
  currentUserId,
  inviteMember,
  listTeam,
  removeMember,
  transferOwnership,
  updateMemberRole,
  type Invitation,
  type Member,
  type MemberRole,
} from "../lib/team.ts";
import { toast } from "../components/Toast.tsx";
import { Select } from "../components/Select.tsx";
import { RowMenu } from "../components/RowMenu.tsx";
import { getOrgUsage, friendlyErrorMessage } from "../lib/api.ts";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function initials(name: string) {
  const parts = name.trim().split(" ");
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

// ---------------------------------------------------------------------------
// Invite modal
// ---------------------------------------------------------------------------

function InviteModal({ orgId, orgName, onClose, onInvited }: { orgId: string; orgName: string; onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("member");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await inviteMember(orgId, email.trim(), role);
      toast(`Invite sent to ${email}`, true);
      onInvited();
    } catch (err) {
      setError(friendlyErrorMessage(err, "Failed to send invitation."));
    } finally {
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3>Invite to {orgName}</h3>
            <p className="sub">They'll get an email to join this organization.</p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <label htmlFor="inviteEmail">Email address</label>
              <input
                className="input"
                id="inviteEmail"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
              />
            </div>
            <div className="field">
              <label htmlFor="inviteRole">Role</label>
              <Select
                value={role}
                ariaLabel="Role"
                options={[{ value: "admin", label: "Admin" }, { value: "member", label: "Member" }]}
                onChange={(v) => setRole(v as MemberRole)}
              />
              <span className="help">You can change this anytime after they join.</span>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="spinner" />Sending…</> : "Send invite"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Transfer-ownership modal
// ---------------------------------------------------------------------------

function TransferModal({ member, busy, onClose, onConfirm }: { member: Member; busy: boolean; onClose: () => void; onConfirm: () => void }) {
  return createPortal(
    <div className="modal-root open" style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3>Transfer ownership</h3>
            <p className="sub">Make <strong style={{ color: "var(--text)" }}>{member.name}</strong> the owner.</p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="modal-body">
          <p className="sub">
            An organization has a single owner. You'll become an <strong style={{ color: "var(--text)" }}>Admin</strong>, and
            {" "}{member.name} will gain full control, including billing and deleting the organization.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={onConfirm}>
            {busy ? <><span className="spinner" />Transferring…</> : "Transfer ownership"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function TeamPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [transferTarget, setTransferTarget] = useState<Member | null>(null);
  // Plan seat limit (the Seats tile's denominator). undefined = not loaded yet,
  // null = unlimited, number = cap. Sourced from the org's plan via usage.
  const [seatLimit, setSeatLimit] = useState<number | null | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  const me = currentUserId();

  const fetchTeam = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    setLoadError(false);
    listTeam(orgId)
      .then((data) => { setMembers(data.members); setInvitations(data.invitations); })
      .catch(() => { setMembers([]); setInvitations([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);
  // Seat limit follows the org's plan (not the team list), so fetch it once per
  // org rather than on every team refresh.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getOrgUsage(orgId)
      .then((u) => { if (active) setSeatLimit(u.seatLimit); })
      .catch(() => { /* cap unknown -> tile shows the seat count without a limit */ });
    return () => { active = false; };
  }, [orgId]);

  async function changeRole(memberId: string, role: MemberRole) {
    if (!orgId) return;
    setBusyId(memberId);
    try {
      await updateMemberRole(orgId, memberId, role);
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, role } : m));
      const name = members.find((m) => m.id === memberId)?.name ?? "Member";
      toast(`${name} is now ${role}`, true);
    } finally {
      setBusyId(null);
    }
  }

  async function handleRemove(memberId: string) {
    if (!orgId) return;
    const name = members.find((m) => m.id === memberId)?.name ?? "Member";
    setBusyId(memberId);
    try {
      await removeMember(orgId, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast(`Removed ${name}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleCancelInvite(invitationId: string) {
    if (!orgId) return;
    setBusyId(invitationId);
    try {
      await cancelInvitation(orgId, invitationId);
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
      toast("Invite revoked");
    } finally {
      setBusyId(null);
    }
  }

  async function handleTransfer(member: Member) {
    if (!orgId) return;
    setBusyId(member.id);
    try {
      await transferOwnership(orgId, member.id);
      toast(`${member.name} is now the owner`, true);
      setTransferTarget(null);
      fetchTeam();
    } finally {
      setBusyId(null);
    }
  }

  const owners = members.filter((m) => m.role === "owner").length;
  const admins = members.filter((m) => m.role === "admin").length;
  // Seats consumed = active members + pending invites (matches the server's seat
  // accounting and the usage page's "Team seats" tile).
  const seatsUsed = members.length + invitations.length;
  // Seats are full once usage hits the plan's cap (null = unlimited; undefined =
  // still loading, so don't pre-disable). Mirrors the server's invite gate so we
  // never surface a misleading "invite sent" for an invite that will be rejected.
  const seatsFull = seatLimit != null && seatsUsed >= seatLimit;
  const iAmOwner = members.some((m) => m.userId === me && m.role === "owner");
  const filteredMembers = search
    ? members.filter((m) => (m.name + m.email).toLowerCase().includes(search.toLowerCase()))
    : members;

  return (
    <div className="animate-fade-in">
      {/* Page header */}
      <div className="page-head rise">
        <div>
          <h1>Team</h1>
          <p className="sub">People with access to this organization. Roles control what each member can do.</p>
        </div>
        <div className="page-head-actions">
          {seatsFull && (
            <span className="hint" style={{ alignSelf: "center" }}>All {seatLimit} seats in use</span>
          )}
          <button
            className="btn btn-primary"
            onClick={() => setShowInvite(true)}
            disabled={seatsFull}
            title={seatsFull ? `All ${seatLimit} seats on your plan are in use. Remove a member or upgrade to add more.` : undefined}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" />
            </svg>
            Invite member
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-4 rise" style={{ marginBottom: "18px" }}>
        <div className="card stat">
          <span className="k">Members</span>
          <span className="v tnum">{members.length}</span>
          <span className="meta">{owners} owner · {members.length - owners} members</span>
        </div>
        <div className="card stat">
          <span className="k">Pending invites</span>
          <span className="v tnum">{invitations.length}</span>
          <span className="meta">{invitations.length > 0 ? "awaiting acceptance" : "none pending"}</span>
        </div>
        <div className="card stat">
          <span className="k">Seats</span>
          <span className="v tnum">{seatLimit == null ? seatsUsed : `${seatsUsed} / ${seatLimit}`}</span>
          <span className="meta">
            {seatLimit === undefined ? "current plan" : seatLimit === null ? "unlimited seats" : "members + pending invites"}
          </span>
        </div>
        <div className="card stat">
          <span className="k">Admins</span>
          <span className="v tnum">{admins}</span>
          <span className="meta">can manage members</span>
        </div>
      </div>

      {/* Members table */}
      {loading ? (
        <div className="skeleton-page">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: "4rem" }} />)}
        </div>
      ) : loadError ? (
        <div className="card rise">
          <div className="error-banner">
            Couldn't load team members.{" "}
            <button className="btn btn-ghost btn-sm" onClick={fetchTeam}>Retry</button>
          </div>
        </div>
      ) : (
        <div className="card rise">
          <div className="card-head">
            <h2>Members</h2>
            <input
              className="input"
              placeholder="Search members…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "220px", padding: "7px 11px", fontSize: "13px" }}
            />
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m) => {
                  const isOwner = m.role === "owner";
                  const isMe = me !== undefined && m.userId === me;
                  return (
                    <tr key={m.id}>
                      <td>
                        <div className="identity">
                          <span className="avatar">{initials(m.name)}</span>
                          <span className="who">
                            <div className="name">
                              {m.name}
                              {isMe && <span className="badge badge-neutral" style={{ marginLeft: "4px" }}>You</span>}
                            </div>
                            <div className="sub">{m.email}</div>
                          </span>
                        </div>
                      </td>
                      <td>
                        {isOwner ? (
                          <span className="badge badge-accent">Owner</span>
                        ) : (
                          <Select
                            value={m.role}
                            disabled={busyId === m.id}
                            ariaLabel={`Role for ${m.name}`}
                            options={[{ value: "admin", label: "Admin" }, { value: "member", label: "Member" }]}
                            onChange={(v) => changeRole(m.id, v as MemberRole)}
                          />
                        )}
                      </td>
                      <td>
                        <span className="badge badge-ok"><span className="dot" />Active</span>
                      </td>
                      <td className="muted">{formatDate(m.createdAt)}</td>
                      <td className="col-right">
                        {!isOwner && !isMe && (
                          <RowMenu
                            ariaLabel="Member actions"
                            items={[
                              ...(iAmOwner
                                ? [{
                                    label: "Make owner",
                                    onClick: () => setTransferTarget(m),
                                    icon: (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2l3 3-3 3" /><path d="M5 11V9a4 4 0 0 1 4-4h6" /><path d="M12 22l-3-3 3-3" /><path d="M19 13v2a4 4 0 0 1-4 4H9" />
                                      </svg>
                                    ),
                                  }]
                                : []),
                              {
                                label: "Remove from org",
                                danger: true,
                                onClick: () => handleRemove(m.id),
                                icon: (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M17 11h6" />
                                  </svg>
                                ),
                              },
                            ]}
                          />
                        )}
                        {isOwner && (
                          <span className="faint" style={{ fontSize: "12px" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending invites */}
      {!loading && invitations.length > 0 && (
        <div className="card mt rise">
          <div className="card-head">
            <h2>Pending invites</h2>
            <span className="hint">{invitations.length} awaiting acceptance</span>
          </div>
          <div className="table-wrap">
            <table className="data">
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <div className="identity">
                        <span className="avatar" style={{ background: "var(--surface)", color: "var(--text-subtle)" }}>?</span>
                        <span className="who">
                          <div className="name">{inv.email}</div>
                          <div className="sub" style={{ textTransform: "capitalize" }}>Invited · expires {new Date(inv.expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-neutral" style={{ textTransform: "capitalize" }}>{inv.role}</span>
                    </td>
                    <td>
                      <span className="badge badge-warn"><span className="dot" />Pending</span>
                    </td>
                    <td className="col-right" style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end" }}>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => toast("Invite re-sent", true)}
                      >
                        Resend
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleCancelInvite(inv.id)}
                        disabled={busyId === inv.id}
                      >
                        {busyId === inv.id ? "Revoking…" : "Revoke"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role legend */}
      <div className="card card-pad mt rise">
        <h3 style={{ fontSize: "14px", marginBottom: "14px" }}>Role permissions</h3>
        <div className="grid grid-3" style={{ gap: "14px" }}>
          <div>
            <div className="row gap-sm" style={{ marginBottom: "5px" }}>
              <span className="badge badge-accent">Owner</span>
            </div>
            <p className="muted" style={{ fontSize: "12.5px" }}>Full access, including billing, deletion and transferring ownership.</p>
          </div>
          <div>
            <div className="row gap-sm" style={{ marginBottom: "5px" }}>
              <span className="badge badge-neutral">Admin</span>
            </div>
            <p className="muted" style={{ fontSize: "12.5px" }}>Manage members, API keys and settings. No billing changes.</p>
          </div>
          <div>
            <div className="row gap-sm" style={{ marginBottom: "5px" }}>
              <span className="badge badge-neutral">Member</span>
            </div>
            <p className="muted" style={{ fontSize: "12.5px" }}>Create and rotate API keys, view usage. No member access.</p>
          </div>
        </div>
      </div>

      {/* Invite modal */}
      {showInvite && orgId && (
        <InviteModal
          orgId={orgId}
          orgName="this organization"
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); fetchTeam(); }}
        />
      )}

      {transferTarget && (
        <TransferModal
          member={transferTarget}
          busy={busyId === transferTarget.id}
          onClose={() => setTransferTarget(null)}
          onConfirm={() => handleTransfer(transferTarget)}
        />
      )}
    </div>
  );
}
